import bustlyAiIcon from '@/assets/icons/bustly_ai.svg';
import { History as HistoryList } from '@/components/History';
import {
    AgentConversation,
    MessageType
} from '@common/api/agentService';
import {
    AgentInput, ContextItem, handleSearch
} from '@common/components/AgentInput';
import {
    Icon,
    Loading, Tooltip
} from '@common/index';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import { Search } from 'lucide-react';
import React, { useEffect } from 'react';
import styles from './ChatSidebar.module.scss';
import { Message } from './Message';

// Define the structure for the UI
interface ChatSidebarUIProps {
  className?: string;
  context?: ContextItem[];
  messages: MessageType[];
  isLoadingConversation?: boolean;
  isLoadingHistory?: boolean;
  history?: AgentConversation[];
  inputValue: string;
  isGenerating?: boolean;
  showToBottom?: boolean;
  profile: {
    userName?: string;
  } | null;
  // Event handlers
  onClose?: () => void;
  onNewChat?: () => void;
  onHistoryItemClick?: (historyItem: AgentConversation) => void;
  onSearch: handleSearch;
  onStopSearch?: () => void;
  onInputChange?: (value: string) => void;
  onScrollToBottom?: () => void;
  onPromptClick?: (prompt: string) => void;
  onScroll?: () => void;
  chatSideBarScrollRef?: React.RefObject<HTMLDivElement>;
  inputRef?: React.RefObject<HTMLTextAreaElement>;
  removeContextItem?: (index: number) => void;
  isEditMode?: boolean;
  
  customPromptSuggestions?: (string | { label: string; icon?: React.ReactNode })[];
}

const ChatSidebarUI: React.FC<ChatSidebarUIProps> = ({
    className,
    messages,
    isLoadingConversation,
    history,
    isLoadingHistory,
    inputValue,
    isGenerating,
    onNewChat,
    onHistoryItemClick,
    onSearch,
    onInputChange,
    onClose,
    chatSideBarScrollRef,
    onScroll,
    onScrollToBottom,
    customPromptSuggestions,
    context,
    removeContextItem,
    onStopSearch,
    showToBottom,
    onPromptClick,
    inputRef,
    profile,
    isEditMode,
}) => {  
    // Auto scroll to bottom
    useEffect(() => {
        if (chatSideBarScrollRef?.current) {
            chatSideBarScrollRef.current.scrollTop = chatSideBarScrollRef.current.scrollHeight;
        }
    }, [messages, isGenerating]);

    const suggestions = customPromptSuggestions || [
        "Identify high-growth dropshipping products",
        "Analyze current market trends for home decor",
        "Find reliable suppliers for pet accessories",
        "Compare shipping costs from different regions"
    ];

    const handlePromptClick = (label: string) => {
        onPromptClick?.(label);
        inputRef?.current?.focus();
    };

    return (
        <div className={classNames(styles.chatSidebar, { [styles.closed]: false }, className)}> 
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <img src={bustlyAiIcon} alt="Bustly.ai" className={styles.logo} />
                    {isEditMode ? 'Build with Bustly AI': 'Bustly AI'} 
                </div>
                <div className={styles.headerRight}>
                    {onNewChat ? (
                        <div className={styles.headerAction}>
                            <Tooltip content="New chat">
                                <button onClick={onNewChat}>
                                    <Icon name="new_chat" size={20} />
                                </button>
                            </Tooltip>
                        </div>
                    ) : null}
          
                    {onHistoryItemClick ? (
                        <DropdownMenu.Root>
                            <DropdownMenu.Trigger asChild>
                                <div className={styles.headerAction}>
                                    <Tooltip content="History">
                                        <button>
                                            <Icon name="history" size={20} />
                                        </button>
                                    </Tooltip>
                                </div>
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Portal>
                                <DropdownMenu.Content className={styles.historyPopup} sideOffset={5} align="end">
                                    <HistoryList 
                                        history={history || []} 
                                        isLoadingHistory={!!isLoadingHistory} 
                                        onHistoryItemClick={onHistoryItemClick}
                                    />
                                </DropdownMenu.Content>
                            </DropdownMenu.Portal>
                        </DropdownMenu.Root>
                    ) : null}

                    {!isEditMode && (
                        <div className={styles.headerAction}>
                            <Tooltip content="Close sidebar">
                                <button onClick={onClose}>
                                    <Icon name="collapse_chat" size={20} />
                                </button>
                            </Tooltip>
                        </div>
                    )}
                </div>
            </div>

            {/* Content */}
            <div 
                className={styles.content} 
                ref={chatSideBarScrollRef}
                onScroll={onScroll}
            >
                {isLoadingConversation ? (
                    <Loading
                        size={24}
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%'
                        }}
                    />
                ) : messages.length === 0 ? (
                    <div className={styles.welcomeSection}>
                        <h3>
                            {isEditMode ? "Refine Dashboard" : `👋 Hi, ${profile?.userName}`}
                        </h3>
                        <p>
                            {isEditMode 
                                ? "Tell me how to improve this dashboard. I can add metrics, change charts, or reorganize the layout."
                                : "Ask anything about your business."}
                        </p>
                        
                        {isEditMode ? <div className={styles.subtitle}>Suggested Edits</div> : null}
            
                        <div className={styles.suggestionGrid}>
                            {suggestions.map((suggestion, idx) => {
                                const isString = typeof suggestion === 'string';
                                const label = isString ? suggestion : suggestion.label;
                                const icon = isString ? <Search size={20} /> : (suggestion.icon || <Search size={20} />);

                                return (
                                    <div key={idx} className={styles.suggestionCard} onClick={() => handlePromptClick(label)}>
                                        <div className={styles.iconWrapper}>
                                            {icon}
                                        </div>
                                        <span>{label}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    <Message messages={messages} isGenerating={!!isGenerating} />
                )}
            </div>

            {/* Input Area */}
            <div className={styles.inputArea}>
                {showToBottom ? (
                    <div className={styles.toBottom} onClick={onScrollToBottom}>
                        <svg width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0.292892 1.70695C-0.0976308 1.31643 -0.0976308 0.683416 0.292892 0.292893C0.683416 -0.0976306 1.31643 -0.0976306 1.70695 0.292893L6.70694 5.29288C7.09746 5.6834 7.09746 6.31641 6.70694 6.70694C6.31642 7.09746 5.6834 7.09746 5.29288 6.70694L0.292892 1.70695Z" fill="white" />
                            <path d="M5.29288 6.70694C4.90236 6.31641 4.90236 5.6834 5.29288 5.29288L10.2929 0.292892C10.6834 -0.0976308 11.3164 -0.0976308 11.7069 0.292892C12.0974 0.683416 12.0974 1.31643 11.7069 1.70695L6.70694 6.70694C6.31642 7.09746 5.6834 7.09746 5.29288 6.70694Z" fill="white" />
                        </svg>
                    </div>
                ) : null}
                <div className={styles.overlay}></div>
                <AgentInput
                    textareaRef={inputRef}
                    handleSearch={onSearch}
                    placeholder="Ask me anything"
                    onStopSearch={onStopSearch}
                    isSearching={isGenerating}
                    showUpload={false}
                    context={context}
                    inputValue={inputValue}
                    setInputValue={onInputChange}
                    onContextRemove={removeContextItem}
                />
            </div>
        </div>
    );
};

export default ChatSidebarUI;
